import { SqlCommand, SubscriptionAccessArchive, SubscriptionAccessStatus } from '@prisma/client';
import { Table } from 'antd';

import { formatIsoDate } from '@app/utils-agnostic';
import { Tables } from '@app/web-components';

import { SubscriptionAccessStatusRenderer } from '../../admin/reports/access-status';
import { useFindSubscriptionAccessArchives } from '../data/subscription-access-archives';

import { EligibilityRenderer } from './eligibility-renderer';

export type SubscriptionAccessArchivePanelProps = {
  subscriptionAccessId: number;
};

export const SubscriptionAccessArchivePanel = ({ subscriptionAccessId }: SubscriptionAccessArchivePanelProps) => {
  const { getQuery, getTableProps } = Tables.useTableState<SubscriptionAccessArchive>(
    'subscription_access_archive_panel',
    [
      {
        title: 'SQL Command',
        dataIndex: 'sqlCommand',
        width: 150,
        render: (sqlCommand: SqlCommand) => {
          switch (sqlCommand) {
            case 'D':
              return 'DELETE';
            case 'I':
              return 'INSERT';
            case 'U':
              return 'UPDATE';
            default:
              break;
          }
        },
      },
      {
        title: 'Status',
        width: 170,
        dataIndex: 'status',
        sorter: true,
        render: (status: SubscriptionAccessStatus) => <SubscriptionAccessStatusRenderer status={status} />,
        filters: [
          { text: 'New', value: SubscriptionAccessStatus.NEW },
          { text: 'Queued', value: SubscriptionAccessStatus.QUEUED },
          { text: 'Retry', value: SubscriptionAccessStatus.RETRY },
          { text: 'Pending', value: SubscriptionAccessStatus.PENDING },
          { text: 'Waiting On Dependency', value: SubscriptionAccessStatus.WAITING_ON_DEPENDENCY },
          { text: 'Completed', value: SubscriptionAccessStatus.COMPLETED },
          { text: 'Cancelled', value: SubscriptionAccessStatus.CANCELLED },
          { text: 'Error', value: SubscriptionAccessStatus.ERROR },
        ],
      },
      {
        title: 'Subscribed',
        width: 140,
        dataIndex: 'subscribed',
        sorter: true,
        render: (subscribed) => <Tables.BooleanRenderer value={subscribed} />,
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
      {
        title: 'Is Eligible',
        width: 150,
        dataIndex: 'isEligible',
        sorter: true,
        render: (isEligible, record) => (
          <EligibilityRenderer isEligible={isEligible} eligibility={record.eligibility} />
        ),
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
      {
        title: 'Status Description',
        dataIndex: 'statusDescription',
        width: 350,
        ellipsis: true,
      },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
    ],
    { disableSession: true },
  );

  const queryResult = useFindSubscriptionAccessArchives(
    getQuery({
      where: { id: subscriptionAccessId },
    }),
  );

  return (
    <Table
      {...getTableProps(queryResult, { rowKey: 'archiveId' })}
      scroll={{
        y: 280,
      }}
    />
  );
};
